import React, { ReactNode } from 'react'
import { Calendar, X, Filter } from 'lucide-react'
import { Button } from './button'
import { SearchFilter } from './SearchFilter'
import { CatalogSelect } from './CatalogSelect'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'

export type FilterType = 'search' | 'branch' | 'status' | 'seller' | 'delivery' | 'payment' | 'invoice' | 'source' | 'dates' | 'bank'
export type DateShortcut = 'today' | 'yesterday' | '1week' | '1month' | '1year' | '5years'

export interface FilterValues {
  search?: string
  branch?: string
  status?: string
  seller?: string
  delivery?: string
  payment?: string
  bank?: string
  invoice?: string
  source?: string
  startDate?: string
  endDate?: string
}

export interface GlobalFilterBarProps {
  /** Array con los nombres de los filtros que se quieren mostrar */
  show?: FilterType[]
  
  /** 
   * Array con los atajos de fecha rápidos que se quieren mostrar.
   * Si no se provee y 'dates' está en show, se muestran todos por defecto.
   */
  dateShortcuts?: DateShortcut[]

  /** Objeto con los valores actuales de los filtros */
  values?: FilterValues

  /** Callback que se ejecuta cuando CUALQUIER filtro cambia */
  onChange?: (changes: Partial<FilterValues>) => void

  /** Opciones personalizadas para los filtros (ej. opciones de 'status', placeholders) */
  customOptions?: {
    searchPlaceholder?: string
    statusOptions?: { value: string; label: string }[]
    statusPlaceholder?: string
    banksOptions?: string[]
  }

  /** Función para limpiar todos los filtros (si hay filtros activos) */
  onClearFilters?: () => void
  hasActiveFilters?: boolean

  /** Filtros extra inyectados manualmente */
  children?: ReactNode
}

export function GlobalFilterBar({
  show = [],
  dateShortcuts = ['today', 'yesterday', '1week', '1month', '1year', '5years'],
  values = {},
  onChange,
  customOptions = {},
  onClearFilters,
  hasActiveFilters = false,
  children
}: GlobalFilterBarProps) {
  
  // Helpers para revisar si un filtro debe mostrarse
  const isVisible = (filter: FilterType) => show.includes(filter)
  const isShortcutVisible = (shortcut: DateShortcut) => dateShortcuts.includes(shortcut)

  // Helper de cambio genérico
  const handleChange = (key: keyof FilterValues, value: string) => {
    if (onChange) onChange({ [key]: value })
  }

  const getISODate = (d: Date) => {
    const offset = d.getTimezoneOffset() * 60000;
    const localDate = new Date(d.getTime() - offset);
    return localDate.toISOString().split('T')[0];
  }

  const setQuickDate = (kind: DateShortcut) => {
    if (!onChange) return
    
    const endD = new Date()
    const startD = new Date()

    switch (kind) {
      case 'today': 
        break;
      case 'yesterday':
        startD.setDate(startD.getDate() - 1)
        endD.setDate(endD.getDate() - 1)
        break;
      case '1week':
        startD.setDate(startD.getDate() - 7)
        break;
      case '1month':
        startD.setMonth(startD.getMonth() - 1)
        break;
      case '1year':
        startD.setFullYear(startD.getFullYear() - 1)
        break;
      case '5years':
        startD.setFullYear(startD.getFullYear() - 5)
        break;
    }
    
    onChange({ 
      startDate: getISODate(startD), 
      endDate: getISODate(endD) 
    })
  }

  const handleClearDates = () => {
    if (onChange) onChange({ startDate: '', endDate: '' })
  }

  const todayStr = getISODate(new Date())
  const yesterdayD = new Date()
  yesterdayD.setDate(yesterdayD.getDate() - 1)
  const yesterdayStr = getISODate(yesterdayD)

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      
      {/* Contenedor de Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        
        {isVisible('search') && (
          <SearchFilter
            initialValue={values.search || ''}
            placeholder={customOptions.searchPlaceholder || 'Buscar...'}
            onSearch={(val: string) => handleChange('search', val)}
          />
        )}

        {isVisible('branch') && (
          <CatalogSelect
            catalog="sucursales"
            value={values.branch || ''}
            onChange={(val) => handleChange('branch', val || '')}
            placeholder="Sucursal"
          />
        )}

        {isVisible('status') && (
          <Select value={values.status || 'all'} onValueChange={(val) => handleChange('status', val)}>
            <SelectTrigger>
              <SelectValue placeholder={customOptions.statusPlaceholder || 'Estado'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {(customOptions.statusOptions || []).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isVisible('seller') && (
          <CatalogSelect
            catalog="users?role=seller"
            value={values.seller || ''}
            onChange={(val) => handleChange('seller', val || '')}
            placeholder="Vendedor"
          />
        )}

        {isVisible('delivery') && (
          <CatalogSelect
            catalog="pos/delivery-users"
            value={values.delivery || ''}
            onChange={(val) => handleChange('delivery', val || '')}
            placeholder="Repartidor"
          />
        )}

        {isVisible('payment') && (
          <CatalogSelect
            catalog="payment-methods"
            value={values.payment || 'all'}
            onChange={(val) => handleChange('payment', val || 'all')}
            placeholder="Método de pago"
          />
        )}

        {isVisible('bank') && (
          <Select value={values.bank || 'all'} onValueChange={(val) => handleChange('bank', val)}>
            <SelectTrigger>
              <SelectValue placeholder="Banco" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los bancos</SelectItem>
              {(customOptions.banksOptions || []).map((bank) => (
                <SelectItem key={bank} value={bank}>{bank}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {isVisible('invoice') && (
          <Select value={values.invoice || 'all'} onValueChange={(val) => handleChange('invoice', val)}>
            <SelectTrigger>
              <SelectValue placeholder="Estado de facturación" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados de facturación</SelectItem>
              <SelectItem value="invoiced">Facturadas</SelectItem>
              <SelectItem value="not_invoiced">Sin facturar</SelectItem>
            </SelectContent>
          </Select>
        )}

        {isVisible('source') && (
          <Select value={values.source || 'all'} onValueChange={(val) => handleChange('source', val)}>
            <SelectTrigger>
              <SelectValue placeholder="Origen de venta" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Cualquier origen</SelectItem>
              <SelectItem value="pos">Punto de Venta</SelectItem>
              <SelectItem value="online" disabled>Tienda en Línea</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* Filtros dinámicos adicionales */}
        {children}

      </div>
      
      {/* Sección opcional de Fechas */}
      {isVisible('dates') && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4 xl:grid-cols-6 pt-3 border-t border-slate-100 mt-3">
          <div className="flex min-w-0 items-center gap-1.5 lg:col-span-1 xl:col-span-1">
            <Calendar className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="text-sm text-slate-500">Desde:</span>
            <input
              type="date"
              value={values.startDate || ''}
              max={values.endDate || todayStr}
              onChange={(e) => handleChange('startDate', e.target.value)}
              className="h-10 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="flex min-w-0 items-center gap-1.5 lg:col-span-1 xl:col-span-1">
            <span className="text-sm text-slate-500">Hasta:</span>
            <input
              type="date"
              value={values.endDate || ''}
              min={values.startDate || ''}
              max={todayStr}
              onChange={(e) => handleChange('endDate', e.target.value)}
              className="h-10 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 lg:col-span-2 xl:col-span-4 xl:justify-end">
            {isShortcutVisible('today') && (
              <button type="button" onClick={() => setQuickDate('today')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === todayStr && values.endDate === todayStr) ? 'border-primary bg-primary text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Hoy</button>
            )}
            
            {isShortcutVisible('yesterday') && (
              <button type="button" onClick={() => setQuickDate('yesterday')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === yesterdayStr && values.endDate === yesterdayStr) ? 'border-primary bg-primary text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Ayer</button>
            )}
            
            {isShortcutVisible('1week') && (
              <button type="button" onClick={() => setQuickDate('1week')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setDate(new Date().getDate() - 7))) && values.endDate === todayStr) ? 'border-primary bg-primary text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Hace 1 Sem</button>
            )}
            
            {isShortcutVisible('1month') && (
              <button type="button" onClick={() => setQuickDate('1month')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setMonth(new Date().getMonth() - 1))) && values.endDate === todayStr) ? 'border-primary bg-primary text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Hace 1 Mes</button>
            )}
            
            {isShortcutVisible('1year') && (
              <button type="button" onClick={() => setQuickDate('1year')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setFullYear(new Date().getFullYear() - 1))) && values.endDate === todayStr) ? 'border-primary bg-primary text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Hace 1 Año</button>
            )}
            
            {isShortcutVisible('5years') && (
              <button type="button" onClick={() => setQuickDate('5years')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${(values.startDate === getISODate(new Date(new Date().setFullYear(new Date().getFullYear() - 5))) && values.endDate === todayStr) ? 'border-primary bg-primary text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Hace 5 Años</button>
            )}
            
            {(values.startDate || values.endDate) && (
              <button type="button" onClick={handleClearDates} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 hover:text-slate-600 transition-colors">
                <X className="mr-1 inline h-3 w-3" />Limpiar fechas
              </button>
            )}
            
            {hasActiveFilters && onClearFilters && (
              <Button type="button" variant="outline" size="sm" onClick={onClearFilters} className="ml-2 h-7 rounded-full text-xs">
                <Filter className="mr-1 h-3 w-3" /> Limpiar
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
